import debounce from 'lodash/debounce';

import { IApiError } from '@frontend/request';

import { EMainProductCodeType } from '@aqx-core/common/api/config';
import { ESymbolCategory, ISymbolDetail } from '@aqx-core/common/api/market';
import { INotificationMetadata } from '@aqx-core/common/api/notification';
import {
  EFillPolicy,
  EOrderHistoryType,
  EOrderStatus,
  EOrderType,
  EOrderWebsocketMessageType,
  IGetOrderByIdV2Params,
  IGetOrderHistoryParams,
  IOrder,
  IOrderHistory,
  IPendingOrder,
  IUIOrderTableDisplay,
  orderAPIController
} from '@aqx-core/common/api/order';
import {
  IBulkCloseMarketOrderParams,
  IBulkClosePendingOrderParams,
  ICloseMarketOrderParams,
  tradeAPIController
} from '@aqx-core/common/api/trade';
import { IAPIControllerCallbacks } from '@aqx-core/common/api/types';
import {
  EColumnPreferenceOrderType,
  EUserPreferenceCategory,
  EUserPreferenceType,
  userAccountSettingAPIController
} from '@aqx-core/common/api/user-account-setting';
import { EWatchlistCategory } from '@aqx-core/common/api/watchlist';
import { getSecondsSinceEpoch } from '@aqx-core/common/dayjs';
import { mapOrderProfitStatusToBulkCloseType } from '@aqx-core/common/format/order';
import { mapResponseCodeToMessage } from '@aqx-core/common/request';
import { assetViewController } from '@aqx-core/common/services/asset/controller';
import { EDateRangeOption, ESortOption, IAssetsFilterValues } from '@aqx-core/common/services/assets-filter/types';
import { copyTradeAssetViewController } from '@aqx-core/common/services/copy-trade/asset';
import { copyTradeService } from '@aqx-core/common/services/copy-trade/service';
import {
  ISymbolTopicLivePrices,
  ITradeRiskPositionLiveData,
  SYM_SUB_KEY_UITRADE_ORDER_LISTING_ITEM,
  symbolTopicSubscription
} from '@aqx-core/common/socket/subscriptions';
import storage, { EStorageKey } from '@aqx-core/common/storage';
import { updateOrdersWithLiveProfits } from '@aqx-core/common/utils/order';

import { assetsFilterViewController } from '../assets-filter/controller';
import { companyService } from '../company';
import { companyViewController } from '../company/controller';
import { copyTradeViewController, isAccountHasSelectedCopier } from '../copy-trade';
import { mixpanelAnalysisService } from '../mixpanel';
import { eventNameHelper } from '../mixpanel/helpers';
import { getOrderHistoryTypeFromSelectedTab } from '../order-table-export/utils';
import { EUISnackBarMessageType, snackbarController } from '../snackbar';
import { symbolPriceService } from '../symbol-price';
import { tradeAnalysisViewController } from '../trade/analysis/controller';
import { equityChartViewController } from '../trade/analysis/equity-chart/controller';
import { tradeWatchlistService } from '../trade/watchlist';
import { tradeWatchlistViewController } from '../trade/watchlist/controller';
import { DefaultFilterSortValue } from './constants';
import { orderListingService } from './service';
import {
  EDeleteOrderType,
  EOrderProfitStatus,
  IOrderHistoryDateRange,
  IWebSocketOrBulkCloseRefreshOrdersParams
} from './types';
import { getMappedOpenPositionsKeyByIntegration } from './utils';

const DEFAULT_LIVE_PRICES: ISymbolTopicLivePrices = {
  liveLow: 0,
  liveHigh: 0,
  liveBuy: 0,
  liveSell: 0,
  liveSpread: 0,
  liveLastPrice: 0
};

class OrderListingViewController {
  private orderSelectedTab: EColumnPreferenceOrderType = EColumnPreferenceOrderType.OPEN_POSITION;
  private orderHistorySelectedTab: EColumnPreferenceOrderType = EColumnPreferenceOrderType.POSITION_HISTORY;

  private symbolIds: string[] = [];
  private mappedSymbolPrices: Record<string, ISymbolTopicLivePrices> = {};
  private profitTrackingOrders: IOrder[] = [];

  private mappedOpenPositions: Record<string, IOrder> = {};
  private mappedPendingOrders: Record<string, IPendingOrder> = {};
  private mappedOrderHistories: Record<string, IOrderHistory> = {};
  private get orderHistoryDateRange() {
    return orderListingService.context.orderHistoryDateRange;
  }

  private get selectedSymbol() {
    return orderListingService.context.selectedSymbol;
  }

  private get initialLoadOpenPositions() {
    return orderListingService.context.initialLoadOpenPositions;
  }

  private get initialLoadPendingOrders() {
    return orderListingService.context.initialLoadPendingOrders;
  }

  private get initialLoadOrderHistories() {
    return orderListingService.context.initialLoadOrderHistories;
  }

  private get initialLoadHistoryDeals() {
    return orderListingService.context.initialLoadHistoryDeals;
  }

  public setFilterValues = (filterValues: IAssetsFilterValues) => {
    orderListingService.setFilterValues(filterValues);
    storage.setItem(EStorageKey.ASSETS_FILTER_VALUES, filterValues);
  };

  public getFilterValues = () => {
    return { ...orderListingService.context.filterFormValues };
  };

  public getOrderById = (id: string, isPendingOrder: boolean) => {
    if (isPendingOrder) {
      return this.mappedPendingOrders[id];
    }
    return this.mappedOpenPositions[id];
  };

  public getOrderSymbolPricesById = (id: string) => {
    return symbolPriceService.context.mappedSymbolPrices[id] || DEFAULT_LIVE_PRICES;
  };

  public getOrderSymbolLivePrice = ({
    id,
    orderType,
    isOpenPosition
  }: {
    id: string;
    orderType?: EOrderType;
    isOpenPosition: boolean;
  }) => {
    const { liveBuy = 0, liveSell = 0 } = this.getOrderSymbolPricesById(id);

    switch (orderType) {
      case EOrderType.SELL:
      case EOrderType.SELL_STOP:
      case EOrderType.SELL_LIMIT:
      case EOrderType.SELL_STOP_LIMIT:
        return isOpenPosition ? liveBuy : liveSell;
      default:
        return isOpenPosition ? liveSell : liveBuy;
    }
  };

  public init = (isTradePage: boolean) => {
    orderListingService.setOrderSelectedTab(this.orderSelectedTab);
    orderListingService.setOrderHistorySelectedTab(this.orderHistorySelectedTab);
    orderListingService.setIsTradePage(isTradePage);
    orderListingService.setIsShowAllSymbolLoaded(false);

    this.loadFilterValuesFromStorage();
    this.queryShowAllSymbol();
  };

  public onSelectedSymbolChanged = (selectedSymbol?: string) => {
    orderListingService.setSelectedSymbol(selectedSymbol);

    if (orderListingService.context.isTradePage && !orderListingService.context.isShowAllSymbolLoaded) {
      return;
    }

    if (
      !orderListingService.context.isShowAllSymbol ||
      (!selectedSymbol && orderListingService.context.isShowAllSymbol)
    ) {
      this.getOrderCounts();
      this.getOrderListings();

      this.initForPriceLadder();
    }
  };

  public refreshAllAssets = async () => {
    await Promise.all([this.getOrderCounts(), this.getOrderListings(), this.initForPriceLadder()]);
  };

  public initForPriceLadder = async () => {
    await this.getPendingOrders();
  };

  public resetInitialLoading = (orderTab: EColumnPreferenceOrderType) => {
    switch (orderTab) {
      case EColumnPreferenceOrderType.OPEN_POSITION:
        orderListingService.setInitialLoadOpenPositions(true);
        break;
      case EColumnPreferenceOrderType.PENDING_ORDER:
        orderListingService.setInitialLoadPendingOrders(true);
        break;
      case EColumnPreferenceOrderType.TRANSACTION_HISTORY:
      case EColumnPreferenceOrderType.ORDER_HISTORY:
      case EColumnPreferenceOrderType.POSITION_HISTORY:
        orderListingService.setInitialLoadOrderHistories(true);
        break;
      case EColumnPreferenceOrderType.ORDERS_AND_DEALS:
        orderListingService.setInitialLoadHistoryDeals(true);
        break;
      default:
        break;
    }
  };

  public resetHistoryOrdersCentroidInitialCall = () => {
    orderListingService.setInitialLoadHistoryOrdersCentroid(true);
  };

  public optimisticUpdateOpenPosition = (orderId: string, updates: { stopLoss?: number; takeProfit?: number }) => {
    const cachedOrder = this.mappedOpenPositions[orderId];
    if (cachedOrder) {
      this.mappedOpenPositions[orderId] = {
        ...cachedOrder,
        stopLoss: updates.stopLoss ?? cachedOrder.stopLoss,
        takeProfit: updates.takeProfit ?? cachedOrder.takeProfit
      };
      orderListingService.setMappedOpenPositions(this.mappedOpenPositions);
    }
  };

  public optimisticUpdatePendingOrder = (
    orderId: string,
    updates: { stopLoss?: number; takeProfit?: number; price?: number; priceTrigger?: number }
  ) => {
    const cachedOrder = this.mappedPendingOrders[orderId];
    if (cachedOrder) {
      this.mappedPendingOrders[orderId] = {
        ...cachedOrder,
        stopLoss: updates.stopLoss ?? cachedOrder.stopLoss,
        takeProfit: updates.takeProfit ?? cachedOrder.takeProfit,
        price: updates.price ?? cachedOrder.price,
        priceTrigger: updates.priceTrigger ?? cachedOrder.priceTrigger
      };
      orderListingService.setMappedPendingOrders(this.mappedPendingOrders);
    }
  };

  public reset = () => {
    orderListingService.reset();
    this.orderSelectedTab = EColumnPreferenceOrderType.OPEN_POSITION;
    this.orderHistorySelectedTab = EColumnPreferenceOrderType.POSITION_HISTORY;
    this.symbolIds = [];
    this.mappedSymbolPrices = {};
    this.profitTrackingOrders = [];
    this.mappedOpenPositions = {};
    this.mappedPendingOrders = {};
    this.mappedOrderHistories = {};
    if (this.symbolPriceFlushTimer !== null) {
      clearTimeout(this.symbolPriceFlushTimer);
      this.symbolPriceFlushTimer = null;
    }
    this.pendingSymbolPriceIds.clear();
  };

  public checkBulkDeletePerformable = (param: {
    orders: (IOrder | IPendingOrder)[];
    symbol?: string;
    assetPage?: boolean;
    closable?: boolean;
  }) => {
    const { orders, symbol, assetPage, closable } = param;
    const { isShowAllSymbol } = orderListingService.context;

    if (!assetPage && !isShowAllSymbol && !closable) {
      return false;
    }

    const ordersCount = (
      !assetPage && !isShowAllSymbol && symbol
        ? orders.filter((o: IOrder | IPendingOrder) => o.symbol === symbol && o.closable === true)
        : orders.filter((o: IOrder | IPendingOrder) => o.closable === true)
    )?.length;

    return ordersCount > 0;
  };

  // User Actions
  public toggleOtherSymbolVisibility = (isVisible: boolean) => {
    this.toggleShowAllSymbol(isVisible, (showAllSymbol: boolean) => {
      const { isAllSymbolFetched } = orderListingService.context;
      if (showAllSymbol && !isAllSymbolFetched) {
        this.getOrderListings();
      }

      this.getOrderCounts();
    });
  };

  public onSelectOrderHistoryDateRange = (dateRange: [Date | null, Date | null], fromStart?: boolean) => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      const newHistoryDateRange: IOrderHistoryDateRange = {
        from: dateRange[0],
        to: dateRange[1],
        fromStart
      };

      orderListingService.setOrderHistoryDateRange(newHistoryDateRange);

      if (this.shouldFetchDealHistories()) {
        this.getOrderAndDealHistories();
      } else {
        this.getOrderHistories(true, this.resolveOrderHistoryType());
      }
    }
  };

  public onSelectOrderSymbol = (symbol: string, symbolType?: ESymbolCategory) => {
    if (!tradeWatchlistService.context.displayWatchlistSymbolIds.includes(symbol)) {
      tradeWatchlistViewController.onWatchlistFilterChanged(EWatchlistCategory.ALL);
      tradeWatchlistViewController.onAllSymbolCategoryChanged(undefined);
      tradeWatchlistViewController.setInitial(false);
    }
    tradeAnalysisViewController.setSymbolDetail({ symbolId: symbol, symbolType });
  };

  private static readonly MAX_OPEN_POSITION_RECONCILE_RETRIES = 2;

  public onOrderSelectedTabChanged = async (tab: EColumnPreferenceOrderType) => {
    if (this.orderSelectedTab === tab) {
      return;
    }

    const eventName = eventNameHelper.mapAssetsOrderTabToClickEventName(tab);
    if (eventName) {
      mixpanelAnalysisService.page.actionClick({
        action: eventName,
        symbol: orderListingService.context.isTradePage ? orderListingService.context.selectedSymbol : undefined // Only pass symbol if not in asset page
      });
    }

    this.orderSelectedTab = tab;
    orderListingService.setOrderSelectedTab(tab);

    this.sanitizeFilterValuesForTab(tab);

    if (tab === EColumnPreferenceOrderType.COPY_TRADE_MISSED_TRADES) {
      copyTradeViewController.getUserInfo({
        onSuccess: (result) => {
          if (result && isAccountHasSelectedCopier(result.linkedCopiers ?? [])) {
            copyTradeAssetViewController.getCopierMissedTrades(copyTradeService.context.selectedCopierId ?? '');
          }
        }
      });
      return;
    }

    this.resetInitialLoading(tab);

    const isHistoryTab = [
      EColumnPreferenceOrderType.ORDER_HISTORY,
      EColumnPreferenceOrderType.POSITION_HISTORY,
      EColumnPreferenceOrderType.ORDERS_AND_DEALS,
      EColumnPreferenceOrderType.TRANSACTION_HISTORY
    ].includes(tab);

    // WT-13665: For the Open Position tab, await both APIs then reconcile mismatches
    // between the count and list responses. Other tabs keep the original fire-and-forget behaviour.
    if (tab === EColumnPreferenceOrderType.OPEN_POSITION) {
      await Promise.all([this.getOrderCounts(), this.getOrderListings()]);
      await this.reconcileOpenPositionData();
    } else if (isHistoryTab) {
      // History tabs: update orderHistorySelectedTab so getOrderListings routes correctly,
      // and skip getOrderCounts (which calls getOpenAndPendingOrderCounts — irrelevant for history).
      this.orderHistorySelectedTab = tab;
      orderListingService.setOrderHistorySelectedTab(tab);
      orderListingService.setOrderHistoryTableDisplay([]);
      this.getOrderListings();
    } else {
      this.getOrderCounts();
      this.getOrderListings();
    }
  };

  /**
   * WT-13665: Reconcile Open Position count vs list data.
   * After the initial parallel fetch, the count API and list API may return inconsistent results.
   *   - Count > 0 but list is empty  → retry the list API
   *   - Count = 0 but list has data  → retry the count API
   *   - Data matches                 → no action
   * Retries up to 2 times (3 total calls including the initial fetch).
   */
  private reconcileOpenPositionData = async () => {
    for (let attempt = 0; attempt < OrderListingViewController.MAX_OPEN_POSITION_RECONCILE_RETRIES; attempt++) {
      const count = orderListingService.context.marketOrderCounts;
      const listLength = orderListingService.context.openPositionsTableDisplay.length;

      if (count > 0 && listLength === 0) {
        await this.getOpenPositions();
      } else if (count === 0 && listLength > 0) {
        await this.getOrderCounts();
      } else {
        break;
      }
    }
  };

  public onOrderHistorySelectedTabChanged = (tab: EColumnPreferenceOrderType) => {
    if (this.orderHistorySelectedTab === tab) {
      return;
    }

    this.orderHistorySelectedTab = tab;
    orderListingService.setOrderHistorySelectedTab(tab);
    orderListingService.setOrderHistoryTableDisplay([]);

    if (this.shouldFetchDealHistories()) {
      this.getOrderAndDealHistories();
    } else {
      this.getOrderHistories(true, this.resolveOrderHistoryType());
    }
  };

  public resetOrderSelectedTab = (tabToResetTo: EColumnPreferenceOrderType | undefined) => {
    this.orderSelectedTab = tabToResetTo || EColumnPreferenceOrderType.OPEN_POSITION;
    orderListingService.setOrderSelectedTab(this.orderSelectedTab);
  };

  private getSelectedSymbolParams = () => {
    const isHorizon = companyService.getCompanyIntegration() === EMainProductCodeType.HORIZON;
    // On Horizon, always show orders without filtering by symbol
    if (isHorizon) {
      return undefined;
    }
    // In trade page, filter orders by currently selected symbol
    if (orderListingService.context.isTradePage && !orderListingService.context.isShowAllSymbol) {
      return this.selectedSymbol;
    }
    // On asset page, show all orders regardless of symbol
    return undefined;
  };

  // API Calls
  private getOrderCounts = async () => {
    await orderAPIController.getOpenAndPendingOrderCounts(
      {
        symbol: this.getSelectedSymbolParams(),
        copyTradingCopierId: copyTradeService.context.selectedCopierId
      },
      {
        onSuccess: (orderCounts) => {
          if (orderCounts) {
            orderListingService.setOrderCounts(orderCounts);
          }
        },
        onError: () => {}
      }
    );
  };

  public getOpenPositions = async (copierId?: string) => {
    orderListingService.setOpenPositionLoading(this.initialLoadOpenPositions);

    const symbolParams = this.getSelectedSymbolParams();

    const companyIntegration = companyService.getCompanyIntegration();

    await orderAPIController.getAllOpenPositions(
      {
        symbol: symbolParams,
        copyTradingCopierId: copyTradeService.context.selectedCopierId ?? copierId
      },
      {
        onSuccess: (orders) => {
          this.symbolIds = [];
          this.mappedOpenPositions = {};

          if (orders) {
            orders.forEach((order) => {
              this.mappedOpenPositions[getMappedOpenPositionsKeyByIntegration(companyIntegration, order)] = order;

              if (!this.symbolIds.includes(order.symbol)) {
                this.symbolIds.push(order.symbol);
                this.mappedSymbolPrices[order.symbol] = {
                  ...DEFAULT_LIVE_PRICES,
                  liveLastPrice: order.lastPrice
                };
              }
            });

            symbolPriceService.setMappedSymbolPrices(this.mappedSymbolPrices);
            orderListingService.setMappedOpenPositions(this.mappedOpenPositions);
            orderListingService.setOpenPositionIds(orders.map((order) => order.orderId));
            orderListingService.setOpenPositionsTableDisplay(orders.map((order) => order));
            this.profitTrackingOrders = orders;
            orderListingService.setIsAllSymbolFetched(symbolParams === undefined);

            this.updateAvailableOrderTypes([
              ...orders,
              ...orderListingService.context.pendingOrdersTableDisplay,
              ...orderListingService.context.orderHistoryTableDisplay
            ]);
            equityChartViewController.updateOrderTrackers();
          } else {
            orderListingService.setMappedOpenPositions(this.mappedOpenPositions);
            orderListingService.setOpenPositionIds([]);
            orderListingService.setOpenPositionsTableDisplay([]);
            orderListingService.setIsAllSymbolFetched(false);
          }
          orderListingService.setInitialLoadOpenPositions(false);
        },
        onError: () => {
          orderListingService.setInitialLoadOpenPositions(false);
        },
        onFinally: () => {
          orderListingService.setOpenPositionLoading(false);
        }
      }
    );
  };

  private getPendingOrders = async () => {
    orderListingService.setPendingOrderLoading(this.initialLoadPendingOrders);

    const symbolParams = this.getSelectedSymbolParams();
    await orderAPIController.getAllPendingOrders(
      { symbol: symbolParams },
      {
        onSuccess: (orders) => {
          this.symbolIds = [];
          this.mappedPendingOrders = {};

          if (orders) {
            orders.forEach((order) => {
              this.mappedPendingOrders[order.orderId] = order;
              if (!this.symbolIds.includes(order.symbol)) {
                this.symbolIds.push(order.symbol);
                this.mappedSymbolPrices[order.symbol] = {
                  ...DEFAULT_LIVE_PRICES,
                  liveLastPrice: order.lastPrice
                };
              }
            });

            symbolPriceService.setMappedSymbolPrices(this.mappedSymbolPrices);
            orderListingService.setMappedPendingOrders(this.mappedPendingOrders);
            orderListingService.setPendingOrderIds(orders.map((order) => order.orderId));
            orderListingService.setPendingOrdersTableDisplay(orders.map((order) => order));
            orderListingService.setIsAllSymbolFetched(symbolParams === undefined);

            this.updateAvailableOrderTypes([
              ...orderListingService.context.openPositionsTableDisplay,
              ...orders,
              ...orderListingService.context.orderHistoryTableDisplay
            ]);

            equityChartViewController.updateOrderTrackers();
          } else {
            orderListingService.setMappedPendingOrders(this.mappedPendingOrders);
            orderListingService.setPendingOrderIds([]);
            orderListingService.setPendingOrdersTableDisplay([]);
            orderListingService.setIsAllSymbolFetched(false);
          }
          orderListingService.setInitialLoadPendingOrders(false);
        },
        onError: () => {
          orderListingService.setInitialLoadPendingOrders(false);
        },
        onFinally: () => {
          orderListingService.setPendingOrderLoading(false);
        }
      }
    );
  };

  private getDealHistories = async (dateRange: IOrderHistoryDateRange) => {
    const { epochFrom, epochTo } = this.convertDateRangeWithUtcOffset(dateRange);

    if (epochFrom === undefined || epochTo === undefined) {
      return;
    }

    orderListingService.setOrderHistoryLoading(this.initialLoadHistoryDeals);
    await orderAPIController.getDealHistories(
      { to: epochTo, from: epochFrom, copyTradingCopierId: copyTradeService.context.selectedCopierId },
      {
        // WT-13754: Update deal histories data to the store so the table re-renders
        onSuccess: (orders) => {
          orders && orderListingService.setOrderHistoryTableDisplay(orders);
        },
        onError: () => {
          orderListingService.setInitialLoadHistoryDeals(false);
        },
        onFinally: () => {
          orderListingService.setOrderHistoryLoading(false);
        }
      }
    );
  };

  private convertDateRangeWithUtcOffset = (dateRange: IOrderHistoryDateRange) => {
    const convertedFrom = companyViewController
      .convertToDayjsWithOffset({
        input: dateRange.from,
        keepTime: true,
        baseFromOffset: true
      })
      .startOf('day');

    const convertedTo = companyViewController
      .convertToDayjsWithOffset({
        input: dateRange.to,
        keepTime: true,
        baseFromOffset: true
      })
      .endOf('day');

    const epochFrom = dateRange.fromStart ? 0 : getSecondsSinceEpoch(convertedFrom);
    const epochTo = getSecondsSinceEpoch(convertedTo);

    return {
      epochFrom,
      epochTo
    };
  };

  private getDefaultOrderHistoryType = () => {
    const companyIntegrationType = companyService.getCompanyIntegration();
    return companyIntegrationType === EMainProductCodeType.HORIZON ||
      companyIntegrationType === EMainProductCodeType.METATRADER4
      ? EOrderHistoryType.ORDER
      : EOrderHistoryType.POSITION;
  };

  private getOrderHistories = async (shouldLoading?: boolean, type?: EOrderHistoryType) => {
    const { epochFrom, epochTo } = this.convertDateRangeWithUtcOffset(this.orderHistoryDateRange);
    if (epochFrom === undefined || epochTo === undefined) {
      return;
    }

    const symbolParams = this.getSelectedSymbolParams();
    const params: IGetOrderHistoryParams = {
      to: epochTo,
      from: epochFrom,
      type: type ?? this.getDefaultOrderHistoryType(),
      symbol: symbolParams,
      copyTradingCopierId: copyTradeService.context.selectedCopierId
    };

    orderListingService.setOrderHistoryLoading(shouldLoading ?? this.initialLoadOrderHistories);

    await orderAPIController.getOrderHistories(params, {
      onSuccess: (orders) => {
        this.symbolIds = [];
        this.mappedOrderHistories = {};

        if (orders) {
          orders.forEach((order) => {
            this.mappedOrderHistories[order.orderId] = order;
          });

          orderListingService.setMappedOrderHistories(this.mappedOrderHistories);
          orderListingService.setOrderHistoryIds(orders.map((order) => order.orderId));
          orderListingService.setOrderHistoryTableDisplay(orders.map((order) => order as IUIOrderTableDisplay));
          orderListingService.setIsAllSymbolFetched(symbolParams === undefined);
          this.updateAvailableOrderTypes([
            ...orderListingService.context.openPositionsTableDisplay,
            ...orderListingService.context.pendingOrdersTableDisplay,
            ...orders
          ]);
        } else {
          orderListingService.setMappedOrderHistories(this.mappedOrderHistories);
          orderListingService.setOrderHistoryIds([]);
          orderListingService.setOrderHistoryTableDisplay([]);
          orderListingService.setIsAllSymbolFetched(false);
        }

        orderListingService.setInitialLoadOrderHistories(false);
        orderListingService.setOrderHistoryLoading(false);
      },
      onError: (apiError: IApiError) => {
        if (!apiError.isAborted) {
          orderListingService.setInitialLoadOrderHistories(false);
          /**
           * EDGE-CASE: When switch tabs the previous request will be aborted.
           * If this action too quick, the loading state couldn't keep updated.
           *
           * We can get away with not setting loading to false in this case.
           * Since when the tab mount again, it will still show loading eventually.
           */
          orderListingService.setOrderHistoryLoading(false);
        }
        orderListingService.setIsAllSymbolFetched(false);
      }
    });
  };

  public bulkCloseMarket = async (params: {
    listOpenPositions: IOrder[];
    ordersMaxSize: number;
    onComplete: (success: boolean, numberOfOrders?: number) => void;
    assetPage?: boolean;
    isWeb?: boolean;
    selectedSymbolDetail?: ISymbolDetail;
    orderProfitStatus?: EOrderProfitStatus;
  }) => {
    const {
      listOpenPositions = [],
      ordersMaxSize,
      onComplete,
      assetPage,
      isWeb,
      selectedSymbolDetail,
      orderProfitStatus
    } = params;
    /**
     * Retrieve the present orders according to the platform type.
     * If the platform is web, it obtains the displayed orders from the context.
     * A maximum of 30 orders is allowed on both the asset and other pages. In the Trading Page, orders will be filtered by symbol, e.g., orders for XRP only.
     * If the platform is not web, it retrieves a slice of the orders from the context, up to a maximum size. Currently mobile display all orders in the Asset Page
     * @returns An array of current orders.
     */

    const profitTrackingOrders = this.profitTrackingOrders;

    if (listOpenPositions.length > 0) {
      orderListingService.setBulkCloseLoading(true);
      const companyIntegration = companyService.getCompanyIntegration() ?? EMainProductCodeType.METATRADER4;

      const orders: IOrder[] = profitTrackingOrders.filter((o) => {
        return listOpenPositions.some((d) => d.orderId === o.orderId);
      });

      // Calculate live profits for all orders using current symbol prices
      // This ensures we have up-to-date profit values even if WebSocket updates haven't been processed yet
      const ordersWithLiveProfits = orders.map((order) => {
        const symbolPrices = this.mappedSymbolPrices[order.symbol];
        if (symbolPrices && (symbolPrices.liveBuy > 0 || symbolPrices.liveSell > 0)) {
          const calculatedProfit = updateOrdersWithLiveProfits([order], order.symbol, symbolPrices)[0].profit;
          return {
            ...order,
            profit: calculatedProfit
          };
        }
        return order;
      });

      const filteredOrders = this.filterOrdersByProfitStatus(ordersWithLiveProfits, orderProfitStatus);

      const orderList: IBulkCloseMarketOrderParams['orderList'] = [];

      filteredOrders.forEach((order) => {
        if (orderList.length >= ordersMaxSize) {
          return;
        }

        if (this.checkShouldPushOrderToBulkClose(order, !!isWeb, !!assetPage, selectedSymbolDetail)) {
          orderList.push(this.createCloseMarketOrderParam(order, companyIntegration));
        }
      });

      mixpanelAnalysisService.order.bulkClosePositionsSubmit({
        symbolDetails: !assetPage && selectedSymbolDetail ? selectedSymbolDetail : undefined,
        numOfPositions: orderList.length,
        bulkCloseType: orderProfitStatus ? mapOrderProfitStatusToBulkCloseType(orderProfitStatus) : undefined
      });
      await tradeAPIController.bulkCloseMarketOrder(
        { orderList },
        {
          onSuccess: (result) => {
            onComplete(true, result?.closedOrderIds?.length || 0);
            orderListingViewController.optimisticallyDeleteOpenOrdersFromTable(orderList.map(({ orderId }) => orderId));
          },
          onError: () => {
            onComplete(false);
          },
          onFinally: () => {
            orderListingService.setBulkCloseLoading(false);
            this.debouncedRefreshOrders({
              origin: 'USER-ACTION',
              refreshOpenOrders: true
            });
          }
        }
      );
    } else {
      onComplete(false);
    }
  };

  public bulkDeletePendingOrders = async (params: {
    listPendingOrders: IPendingOrder[];
    ordersMaxSize: number;
    onComplete: (success: boolean, numberOfOrdersToDelete?: number) => void;
    assetPage?: boolean;
    isWeb?: boolean;
    selectedSymbolDetail?: ISymbolDetail;
    deleteOrderType?: EDeleteOrderType;
  }) => {
    const {
      listPendingOrders = [],
      ordersMaxSize,
      onComplete,
      assetPage,
      isWeb,
      selectedSymbolDetail,
      deleteOrderType
    } = params;

    const { isShowAllSymbol, isTradePage } = orderListingService.context;

    const filteredDisplayData = isTradePage
      ? listPendingOrders.filter((order) => {
          return !isShowAllSymbol ? order.symbol === selectedSymbolDetail?.symbol : true;
        })
      : [...listPendingOrders];

    const integration = companyService.getCompanyIntegration();
    const isCentroid = integration === EMainProductCodeType.CENTROID;

    const pendingOrders = this.filterPendingOrdersByDeleteOrderType(
      filteredDisplayData as IPendingOrder[],
      deleteOrderType
    );

    if (pendingOrders.length > 0) {
      orderListingService.setBulkCloseLoading(true);

      const orderIdList: IBulkClosePendingOrderParams[] = [];
      pendingOrders.forEach((order) => {
        if (orderIdList.length >= ordersMaxSize) {
          return;
        }

        if (this.checkShouldPushOrderToBulkClose(order, !!isWeb, !!assetPage, selectedSymbolDetail)) {
          orderIdList.push({ orderId: isCentroid ? order.id.toString() : order.orderId, symbol: order.symbol });
        }
      });

      mixpanelAnalysisService.order.bulkDeletePendingOrdersSubmit({
        symbolDetails: !assetPage && selectedSymbolDetail ? selectedSymbolDetail : undefined,
        numOfPositions: orderIdList.length
      });

      await tradeAPIController.bulkDeletePendingOrder(orderIdList, {
        onSuccess: (result) => {
          onComplete(true, result?.deletedOrderIds?.length || 0);
        },
        onError: () => {
          onComplete(false);
        },
        onFinally: () => {
          orderListingService.setBulkCloseLoading(false);
          this.debouncedRefreshOrders({
            origin: 'USER-ACTION',
            refreshOpenOrders: false
          });
        }
      });
    }
  };

  public updateOrderProfit = (orderId: string, profit?: number) => {
    this.profitTrackingOrders = this.profitTrackingOrders.map((o) => {
      if (o.orderId === orderId && profit) {
        return { ...o, profit };
      }
      return o;
    });
  };

  private maybeShowLoadingOnRefresh = (
    actionOrigin: IWebSocketOrBulkCloseRefreshOrdersParams['origin'],
    orderTab: EColumnPreferenceOrderType
  ) => {
    // Don't show loading on socket triggered actions
    if (actionOrigin === 'SOCKET') {
      return;
    }
    // Show loading for user's action
    this.resetInitialLoading(orderTab);
  };

  public webSocketOrBulkCloseRefreshOrders = async (params: IWebSocketOrBulkCloseRefreshOrdersParams) => {
    const { origin, refreshOpenOrders, refreshOrderHistory } = params || {};

    // Do not refresh the orders when bulk close is loading
    if (orderListingService.context.bulkCloseLoading) {
      return;
    }

    const integration = companyService.getCompanyIntegration();
    const isCentroid = integration === EMainProductCodeType.CENTROID;

    this.getOrderCounts();
    if (refreshOpenOrders) {
      this.maybeShowLoadingOnRefresh(origin, EColumnPreferenceOrderType.OPEN_POSITION);
      await this.getOpenPositions();
    }
    // Refresh pending orders when:
    // 1. Triggered by user action AND NOT refreshing open orders (i.e., user placed/modified/deleted limit/stop orders)
    // 2. Triggered by websocket (any order change may affect pending orders, including when pending orders match price)
    if (!refreshOpenOrders || origin === 'SOCKET') {
      this.maybeShowLoadingOnRefresh(origin, EColumnPreferenceOrderType.PENDING_ORDER);
      this.getPendingOrders();
    }

    if (refreshOrderHistory) {
      // WT-13754: Only refresh the data source that matches the active history tab.
      // getOrderHistories and getDealHistories both write to orderHistoryTableDisplay.
      // Running them in parallel causes a race condition where getOrderHistories can
      // resolve last and overwrite the deal data (which contains the deals array needed
      // for the expand/collapse chevron), making chevrons disappear intermittently.
      if (this.shouldFetchDealHistories()) {
        this.maybeShowLoadingOnRefresh(origin, EColumnPreferenceOrderType.ORDERS_AND_DEALS);
        this.getDealHistories(this.orderHistoryDateRange);
      } else {
        this.maybeShowLoadingOnRefresh(origin, EColumnPreferenceOrderType.ORDER_HISTORY);
        this.getOrderHistories(undefined, this.resolveOrderHistoryType());
      }

      if (isCentroid) {
        this.resetHistoryOrdersCentroidInitialCall();
        // TODO: Implement softRefreshOrderHistoryCentroid
        // this.softRefreshOrderHistoryCentroid();
      }
    }
    assetViewController.getAccountStatistics();
    assetViewController.getDailyStatistics();
    // TODO: implement setDisableUpdateTrackOrder
    // this.setDisableUpdateTrackOrder(false);
  };

  public debouncedRefreshOrders = debounce((params: IWebSocketOrBulkCloseRefreshOrdersParams) => {
    this.webSocketOrBulkCloseRefreshOrders(params);
  }, 300);

  private getOrderAndDealHistories = async () => {
    const { epochFrom, epochTo } = this.convertDateRangeWithUtcOffset(this.orderHistoryDateRange);

    if (epochFrom === undefined || epochTo === undefined) {
      return;
    }
    orderListingService.setOrderHistoryLoading(true);
    await orderAPIController.getDealHistories(
      {
        from: epochFrom,
        to: epochTo,
        copyTradingCopierId: copyTradeService.context.selectedCopierId
      },
      {
        onSuccess: (orders) => {
          orders && orderListingService.setOrderHistoryTableDisplay(orders);
        },
        onError: () => {},
        onFinally: () => {
          orderListingService.setOrderHistoryLoading(false);
        }
      }
    );
  };

  // Sockets
  public subscribeToSymbolTopics = (symbolIds: string[]) => {
    symbolIds.forEach((symbolId) => {
      symbolTopicSubscription.subscribe(symbolId, SYM_SUB_KEY_UITRADE_ORDER_LISTING_ITEM, this.updateOrderSymbolPrices);
    });
  };

  public unsubscribeToSymbolTopics = (symbolIds: string[]) => {
    symbolIds.forEach((symbolId) => {
      symbolTopicSubscription.unsubscribe(
        symbolId,
        SYM_SUB_KEY_UITRADE_ORDER_LISTING_ITEM,
        this.updateOrderSymbolPrices
      );
    });
  };

  public handleOrderPayloadFromMetaTraderSocket = (orderDetail: Partial<INotificationMetadata>) => {
    const { horizonOrderStatus, type } = orderDetail || {};

    const companyIntegration = companyService.getCompanyIntegration();
    const isHorizon = companyIntegration === EMainProductCodeType.HORIZON;

    if (isHorizon && (!orderDetail || !horizonOrderStatus || horizonOrderStatus === EOrderStatus.PENDING_NEW)) {
      this.handleRefetchOrderRelatedData();
    } else {
      this.debouncedRefreshOrders({
        origin: 'SOCKET',
        // Always refresh open orders - any order type can result in a position
        // (market orders execute immediately, limit/stop orders can fill instantly if price conditions are met)
        refreshOpenOrders: true,
        refreshOrderHistory:
          type === EOrderWebsocketMessageType.CLOSE_ORDER || type === EOrderWebsocketMessageType.UPDATE_ORDER
      });
    }
  };

  public handleRefetchOrderRelatedData = () => {
    this.getOrderCounts();
    this.getOpenPositions();
    this.getPendingOrders();
    // WT-13754: Use getOrderListings to respect the active tab and avoid
    // overwriting deal data when the user is on Orders & Deals.
    this.getOrderListings();
  };

  public updateMappedOpenPositions = (updatedOpenPosition: ITradeRiskPositionLiveData) => {
    const { symbol } = updatedOpenPosition;
    const openPositions = this.mappedOpenPositions[symbol];
    if (openPositions) {
      this.mappedOpenPositions[symbol] = { ...openPositions, ...updatedOpenPosition };
    }
    orderListingService.setMappedOpenPositions(this.mappedOpenPositions);
  };

  public getOrderDetail = async (params: IGetOrderByIdV2Params, callbacks?: IAPIControllerCallbacks<IOrder>) => {
    orderListingService.setOrderDetailLoading(true);
    await orderAPIController.getOrderDetailV2(params, {
      onSuccess: (data) => {
        data && orderListingService.setOrderDetail(data);
        callbacks?.onSuccess?.(data);
      },
      onError: (error) => {
        orderListingService.setOrderDetail(undefined);
        callbacks?.onError?.(error);
      },
      onFinally: () => {
        orderListingService.setOrderDetailLoading(false);
        callbacks?.onFinally?.();
      }
    });
  };

  public resetOrderDetail = () => {
    orderListingService.setOrderDetail(undefined);
  };

  // Filter pending order base on delete pending order action type
  public filterPendingOrdersByDeleteOrderType(
    orders: IPendingOrder[],
    deleteOrderType?: EDeleteOrderType
  ): IPendingOrder[] {
    switch (deleteOrderType) {
      case EDeleteOrderType.LIMIT:
        return orders.filter((o) => o.orderType === EOrderType.BUY_LIMIT || o.orderType === EOrderType.SELL_LIMIT);
      case EDeleteOrderType.STOP:
        return orders.filter((o) => o.orderType === EOrderType.BUY_STOP || o.orderType === EOrderType.SELL_STOP);
      case EDeleteOrderType.STOP_LIMIT:
        return orders.filter(
          (o) => o.orderType === EOrderType.BUY_STOP_LIMIT || o.orderType === EOrderType.SELL_STOP_LIMIT
        );
      default:
        return orders;
    }
  }

  private updateOrderSymbolPrices = (id: string, livePrices: Partial<ISymbolTopicLivePrices>) => {
    const symbolPrices = this.mappedSymbolPrices[id];

    if (symbolPrices) {
      this.mappedSymbolPrices[id] = { ...symbolPrices, ...livePrices };

      // Keep profitTrackingOrders in sync as a plain field (no reactive write)
      const mergedLivePrices = this.mappedSymbolPrices[id];
      this.profitTrackingOrders = updateOrdersWithLiveProfits(this.profitTrackingOrders, id, mergedLivePrices);

      this.pendingSymbolPriceIds.add(id);
      this.scheduleSymbolPriceFlush();
    }
  };

  private pendingSymbolPriceIds = new Set<string>();
  private symbolPriceFlushTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly SYMBOL_PRICE_FLUSH_MS = 200;

  private scheduleSymbolPriceFlush = () => {
    if (this.symbolPriceFlushTimer !== null) {
      return;
    }
    this.symbolPriceFlushTimer = setTimeout(() => {
      this.symbolPriceFlushTimer = null;
      const updates: Record<string, ISymbolTopicLivePrices> = {};
      for (const id of this.pendingSymbolPriceIds) {
        updates[id] = this.mappedSymbolPrices[id];
      }
      symbolPriceService.setMappedSymbolPricesPartial(updates);
      this.pendingSymbolPriceIds.clear();
    }, OrderListingViewController.SYMBOL_PRICE_FLUSH_MS);
  };

  private resolveOrderHistoryType = () => {
    return getOrderHistoryTypeFromSelectedTab(this.orderHistorySelectedTab);
  };

  private shouldFetchDealHistories = () => {
    return this.orderHistorySelectedTab === EColumnPreferenceOrderType.ORDERS_AND_DEALS;
  };

  private getOrderListings = async () => {
    switch (this.orderSelectedTab) {
      case EColumnPreferenceOrderType.OPEN_POSITION:
        await this.getOpenPositions();
        break;
      case EColumnPreferenceOrderType.PENDING_ORDER:
        await this.getPendingOrders();
        break;
      case EColumnPreferenceOrderType.COPY_TRADE_MISSED_TRADES:
        copyTradeViewController.getUserInfo({
          onSuccess: () => {
            copyTradeAssetViewController.getCopierMissedTrades(copyTradeService.context.selectedCopierId ?? '');
          }
        });
        break;
      // All history tabs — covers both web (nested sub-tabs) and mobile (flat top-level tabs)
      case EColumnPreferenceOrderType.ORDER_HISTORY:
      case EColumnPreferenceOrderType.POSITION_HISTORY:
      case EColumnPreferenceOrderType.TRANSACTION_HISTORY:
      case EColumnPreferenceOrderType.ORDERS_AND_DEALS:
        if (this.shouldFetchDealHistories()) {
          await this.getOrderAndDealHistories();
        } else {
          await this.getOrderHistories(undefined, this.resolveOrderHistoryType());
        }
        break;
      default:
        break;
    }
  };

  // Filter orders base on its profit status
  private filterOrdersByProfitStatus(orders: IOrder[], orderProfitStatus?: EOrderProfitStatus): IOrder[] {
    switch (orderProfitStatus) {
      case EOrderProfitStatus.PROFIT:
        return orders.filter((o) => o.profit > 0);
      case EOrderProfitStatus.LOSS:
        return orders.filter((o) => o.profit < 0);
      default:
        return orders;
    }
  }

  // To determine the params inside the order object, different integrations have different params
  private createCloseMarketOrderParam(
    order: IOrder,
    companyIntegration: EMainProductCodeType
  ): ICloseMarketOrderParams {
    const closeMarketParam: ICloseMarketOrderParams = {
      orderId: order.orderId,
      symbol: order.symbol,
      lotSize: order.lotSize
    };

    const isMT5 = companyIntegration === EMainProductCodeType.METATRADER5;
    const isCentroid = companyIntegration === EMainProductCodeType.CENTROID;

    // MT5 and Centroid require fillPolicy
    if (isMT5 || isCentroid) {
      if (order.marketFillPolicy && order.marketFillPolicy.length > 0) {
        closeMarketParam.fillPolicy = order.marketFillPolicy.includes(order.fillPolicy)
          ? order.fillPolicy
          : order.marketFillPolicy[0];
      } else {
        closeMarketParam.fillPolicy = EFillPolicy.FOK;
      }
    }

    // Centroid additionally requires orderType
    if (isCentroid) {
      // Open positions can only have BUY or SELL orderType, cast is safe
      closeMarketParam.orderType = order.orderType as EOrderType.BUY | EOrderType.SELL;
    }

    return closeMarketParam;
  }

  private checkShouldPushOrderToBulkClose = (
    order: IOrder | IPendingOrder,
    isWeb: boolean,
    assetPage: boolean,
    selectedSymbolDetail?: ISymbolDetail
  ) => {
    // Web: orders are already filtered by page context (asset or trade page)
    if (isWeb) {
      return order.closable;
    }

    // Mobile case
    // Asset page: no need to check symbol, just check if closable
    if (assetPage) {
      return order.closable;
    }

    // Mobile trade page: check if hiding other symbols
    const isHidingOtherSymbols = !orderListingService.context.isShowAllSymbol;

    // If hiding other symbols and have selected symbol, enforce symbol match
    if (isHidingOtherSymbols && selectedSymbolDetail) {
      return order.closable && order.symbol === selectedSymbolDetail.symbol;
    }

    // Otherwise just check if closable
    return order.closable;
  };

  public optimisticallyDeleteOpenOrdersFromTable(orderIds: string[]) {
    orderListingService.setOpenPositionsTableDisplay(
      orderListingService.context.openPositionsTableDisplay.filter((item) => !orderIds.includes(item?.orderId))
    );
  }

  public optimisticallyDeletePendingOrdersFromTable(orderIds: string[]) {
    orderListingService.setPendingOrdersTableDisplay(
      orderListingService.context.pendingOrdersTableDisplay.filter((item) => !orderIds.includes(item?.orderId))
    );
  }

  // Prevent race condition: defer order fetching on trade page until this preference is resolved,
  // so orders and counts are always fetched with the correct symbol filter.
  private async queryShowAllSymbol() {
    const onSettled = (isShowAll: boolean) => {
      orderListingService.setIsShowAllSymbol(isShowAll);
      orderListingService.setIsShowAllSymbolLoaded(true);
      this.getOrderCounts();
      this.getOrderListings();
    };

    userAccountSettingAPIController.getUserPreference(
      {
        category: EUserPreferenceCategory.MARKET,
        type: EUserPreferenceType.HIDE_OTHER_SYMBOLS
      },
      {
        onSuccess: (hideSymbolSetting) => {
          if (hideSymbolSetting && hideSymbolSetting.length > 0) {
            const isHideAllSymbols = hideSymbolSetting[0].value && hideSymbolSetting[0].value === 'true';
            onSettled(!isHideAllSymbols);
          } else {
            onSettled(true);
          }
        },
        onError: () => {
          onSettled(true);
        }
      }
    );
  }

  private async toggleShowAllSymbol(isVisible: boolean, onSuccessCallback: (showAllSymbol: boolean) => void) {
    const hideOtherSymbolsBool = isVisible ? 'false' : 'true';
    userAccountSettingAPIController.updateUserPreference(
      {
        category: EUserPreferenceCategory.MARKET,
        type: EUserPreferenceType.HIDE_OTHER_SYMBOLS,
        value: hideOtherSymbolsBool
      },
      {
        onSuccess: () => {
          orderListingService.setIsShowAllSymbol(isVisible);
          onSuccessCallback(isVisible);
        },
        onError: ({ code, message }) => {
          // Show error toast if toggling is unsuccessful
          snackbarController.showAlert({
            message: mapResponseCodeToMessage(code, message),
            messageType: EUISnackBarMessageType.ERROR
          });
        }
      }
    );
  }

  public setSearchText(value: string) {
    orderListingService.setSearchText(value);
  }

  private updateAvailableOrderTypes(allOrders: Array<{ orderType?: EOrderType }>) {
    assetsFilterViewController.updateAvailableOrderTypes(allOrders);
  }

  private sanitizeFilterValuesForTab = (tab: EColumnPreferenceOrderType) => {
    const integration = companyService.getCompanyIntegration();
    if (integration !== EMainProductCodeType.CENTROID) {
      return;
    }

    const currentFilter = orderListingService.context.filterFormValues;
    if (!currentFilter) {
      return;
    }

    const profitLossSortOptions = new Set([ESortOption.PROFIT_LOSS_ASCENDING, ESortOption.PROFIT_LOSS_DESCENDING]);
    const isPendingOrOrderHistory =
      tab === EColumnPreferenceOrderType.PENDING_ORDER || tab === EColumnPreferenceOrderType.ORDER_HISTORY;

    const shouldResetSort =
      tab === EColumnPreferenceOrderType.OPEN_POSITION ||
      (isPendingOrOrderHistory && currentFilter.sortBy && profitLossSortOptions.has(currentFilter.sortBy));

    const shouldResetOrderType =
      tab !== EColumnPreferenceOrderType.PENDING_ORDER && tab !== EColumnPreferenceOrderType.ORDER_HISTORY;
    const shouldResetDateRange = tab !== EColumnPreferenceOrderType.TRANSACTION_HISTORY;

    if (shouldResetSort || shouldResetOrderType || shouldResetDateRange) {
      this.setFilterValues({
        ...currentFilter,
        ...(shouldResetSort && { sortBy: ESortOption.LATEST }),
        ...(shouldResetOrderType && { orderType: DefaultFilterSortValue.orderType }),
        ...(shouldResetDateRange && { dateRange: DefaultFilterSortValue.dateRange })
      });
    }
  };

  private isValidFilterValues = (values: IAssetsFilterValues): boolean => {
    const validSymbolCategories = new Set(Object.values(ESymbolCategory));
    const validSortOptions = new Set(Object.values(ESortOption));
    const validDateRangeOptions = new Set(Object.values(EDateRangeOption));

    if (values.assetTypes?.some((t) => !validSymbolCategories.has(t))) {
      return false;
    }
    if (values.currentAssetType && !validSymbolCategories.has(values.currentAssetType)) {
      return false;
    }
    if (values.sortBy && !validSortOptions.has(values.sortBy)) {
      return false;
    }
    if (values.dateRange?.value && !validDateRangeOptions.has(values.dateRange.value)) {
      return false;
    }

    return true;
  };

  private sanitizeFilterValues = (values: IAssetsFilterValues): IAssetsFilterValues => ({
    ...values,
    assetTypes: values.assetTypes?.filter((t): t is ESymbolCategory => t !== null && t !== undefined)
  });

  private loadFilterValuesFromStorage = () => {
    const savedFilterValues = storage.getItem<IAssetsFilterValues>(EStorageKey.ASSETS_FILTER_VALUES);

    if (!savedFilterValues) {
      return;
    }

    const sanitized = this.sanitizeFilterValues(savedFilterValues);

    if (!this.isValidFilterValues(sanitized)) {
      storage.removeItem(EStorageKey.ASSETS_FILTER_VALUES);
      return;
    }

    orderListingService.setFilterValues({
      ...sanitized,
      dateRange: sanitized.dateRange
        ? {
            ...sanitized.dateRange,
            from: sanitized.dateRange.from ? new Date(sanitized.dateRange.from) : undefined,
            to: sanitized.dateRange.to ? new Date(sanitized.dateRange.to) : undefined
          }
        : undefined
    });
  };
}

export const orderListingViewController = new OrderListingViewController();
